import { Fragment, useState, ChangeEvent, ReactElement, useEffect } from "react";
import { getURLParams } from "../../shared/utils";
import { ENVIRONMENTS } from "@bronte/viewer-api/src/main";

import "./styles.scss";

interface EnvironmentFormTypes {
  autoOpen: boolean;
}

export const EnvironmentForm = ({ autoOpen }: EnvironmentFormTypes): ReactElement => {
  const urlParams = getURLParams();

  const [env, setEnv] = useState(urlParams.env);
  const [pocEnabled, setPocEnabled] = useState(urlParams.pocEnabled === "false");
  const [disableIES, setDisableIES] = useState(urlParams.disableIES === "false");

  const [screenId, setScreenId] = useState(urlParams.screenId);
  const [userId, setUserId] = useState(urlParams.userId);
  const [token, setToken] = useState(urlParams.token);
  const [deploymentURL, setDeploymentURL] = useState(
    `${urlParams.cohortId}/${urlParams.deploymentId}`,
  );

  const [packagePath, setPackagePath] = useState(urlParams.packagePath);
  const [displayEnvChangeSection, setDisplayEnvChangeSection] = useState(false);

  useEffect(() => {
    if (autoOpen) {
      setDisplayEnvChangeSection(!displayEnvChangeSection);
    }
  }, [autoOpen]);

  const handleEnvChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setEnv(event.target.value);
  };

  const handleURLChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDeploymentURL(event.target.value);
  };

  const handlePackagePathChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPackagePath(event.target.value);
  };

  const handleWebSubmit = () => {
    if (!deploymentURL) {
      alert("please enter url");
      return;
    }
    let url = `${window.location.origin}/${deploymentURL}?env=${env}`;

    if (disableIES) {
      if (!(userId && token)) {
        alert("Enter user id and token both.");
        return;
      }
      url += `&disableIES=${disableIES}&userId=${userId}&token=${token}`;
    }
    if (pocEnabled) {
      url += `&pocEnabled=${pocEnabled}${screenId ? `&screenId=${screenId}` : ""}`;
    }

    window.location.href = url;

    if (urlParams.env !== env && window.piSession) {
      //set require login to false, for enabling logout call to redirect to specific url
      // if require login is true, logout call not redirecting to specific url as mentioned in session js docs also
      window.piSession.setOptions({
        requireLogin: false,
      });
      // if not same env logout first to avoid session undefined on switching back to same env next time
      window.piSession.logout(url);
    } else {
      window.location.href = url;
    }
  };

  const handleMobileSubmit = () => {
    if (!packagePath) {
      alert("Enter package path.");
      return;
    }

    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    searchParams.set("packagePath", packagePath);

    url.search = searchParams.toString();

    const newUrl = url.toString();

    window.location.href = newUrl;
  };

  const toggleEnvChangeSection = () => {
    setDisplayEnvChangeSection(!displayEnvChangeSection);
  };

  return (
    <Fragment>
      <button
        type="button"
        className="env-toggle-button"
        onClick={toggleEnvChangeSection}
        aria-label="Switch environment or course"
      >
        <svg
          className="env-toggle-button__icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width={22}
          height={22}
          aria-hidden
          focusable="false"
        >
          <path
            fill="currentColor"
            d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.07.63-.07.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"
          />
        </svg>
      </button>

      {displayEnvChangeSection && (
        <div className="environment-form">
          <form>
            <div>
              <label>Select Environment</label>
              <select value={env} onChange={handleEnvChange}>
                <option value={ENVIRONMENTS.DEV}>Dev</option>
                <option value={ENVIRONMENTS.QA}>QA int</option>
                <option value={ENVIRONMENTS.STG}>Stage</option>
                <option value={ENVIRONMENTS.PPE}>Ppe</option>
                <option value={ENVIRONMENTS.PROD}>Prod</option>
              </select>
            </div>
            <div>
              <label>Enter URL</label>
              <input
                type="text"
                value={deploymentURL}
                onChange={handleURLChange}
                placeholder="cohortId/deploymentId"
              />
              <div className="example-url">
                Example (stg):
                7cb48310-7e24-11ec-9972-017a7c2abbdd/84ef7210-7e24-11ec-9972-017a7c2abbdd <br />
                Example (dev):
                5a1beb80-0228-11ed-b2f5-036476aaf1ec/9bb55ec0-09ec-11ed-972e-4dccf421768b <br />
                Example (qaint):
                53995dc0-0871-11ed-9af5-cd0858a07bff/5f43a9f0-0871-11ed-9af5-cd0858a07bff <br />
                f13c1e71-6f71-11ed-8268-4939351eb58e/f13f52c0-6f71-11ed-8268-4939351eb58e <br />
                Example (ppe):
                e67ab1e0-a17c-11ec-98b4-59fa254ca097/f669d3b0-a17c-11ec-98b4-59fa254ca097 <br />
                Example (prod):
                63208ca0-eb3e-11ec-af3a-5d3ac6ba0af5/69d5ed10-eb3e-11ec-af3a-5d3ac6ba0af5
              </div>
            </div>
            <div>
              <label>Disable IES</label>
              <input
                type="checkbox"
                checked={disableIES}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setDisableIES(isChecked);
                }}
              />
            </div>
            {disableIES && (
              <Fragment>
                <div>
                  <div className="example-url black">
                    <h3>Provide following values for testing without IES.</h3>
                  </div>
                </div>
                <div>
                  <label>Enter PI User Id</label>
                  <input
                    type="text"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="PI User Id"
                  />
                </div>
                <div>
                  <label>Enter Token</label>
                  <input
                    type="text"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="PI Token / bearer token"
                  />
                </div>
              </Fragment>
            )}
            <div>
              <label>Enable POC Learnspace Services</label>
              <input
                type="checkbox"
                checked={pocEnabled}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setPocEnabled(isChecked);
                }}
              />
            </div>

            {pocEnabled && (
              <div>
                <label>Enter Screen Id</label>
                <input
                  type="text"
                  value={screenId}
                  onChange={(e) => setScreenId(e.target.value)}
                  placeholder="Optional - enter screen id to test non default case"
                />
              </div>
            )}
            <div>
              <button type="button" onClick={handleWebSubmit}>
                Submit
              </button>
            </div>
          </form>

          <form>
            <div>
              <label>Enter Offline Package Path</label>
              <input
                type="text"
                value={packagePath}
                onChange={handlePackagePathChange}
                placeholder=""
              />
            </div>
            <div>
              <button type="button" onClick={handleMobileSubmit}>
                Submit
              </button>
            </div>
          </form>
        </div>
      )}
    </Fragment>
  );
};
